import { RecordMap, useAuditEnricher } from "@/Shared";
import { UserModel } from "../UserModel"

/**
 * useUserEnricher hook – aggregates and enriches User domain data.
 * 
 * Author: Samano CASTRE
 * Date: January 29, 2026
 * 
 * Responsibilities:
 * - Fetches User-related dependencies (users, needs, properties, matchings)
 * - Enriches User entities with audit info and agent/user relations
 * - Combines multiple async states into a single `response` object
 * - Exposes utility methods for retrieving enriched contacts (single or list)
 * 
 * Behavior:
 * - Resolves agent user from `agentId`
 * - Links related needs and matchings to each contact
 * - Safely handles missing dependencies (e.g., empty lists)
 * - Provides a unified API for higher-level hooks or UI components
 */
export function useUserEnricher() {

    /**
    * Centralized audit enrichment hook
    * - Provides O(1) audit user resolution via precomputed Map
    * - Exposes audit status and error
    */
    const {
        enricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<UserModel>();


    /**
     * Enriches a single User entity
     * - Adds audit data (createdBy, updatedBy, etc.)
     * - Resolves agent from user list
     * - Attaches related needs and matchings
     */
    const getEnrichedUser = (contact?: UserModel): UserModel | undefined => {
        return contact ? getStrictEnrichedUser(contact) : undefined;
    }

    /**
     * Enriches a single User entity
     * - Adds audit data (createdBy, updatedBy, etc.)
     * - Resolves agent from user list
     * - Attaches related needs and matchings
     */
    const getStrictEnrichedUser = (contact: UserModel): UserModel => {
        return {
            ...getEnrichedAudit(contact),
            manager: contact.managerId ? getEnrichedUserById(contact.managerId) : undefined,
        }
    }

    const getEnrichedUserMap = (userMap: RecordMap<UserModel>): RecordMap<UserModel> => {
                            
        return userMap.map<UserModel>(getStrictEnrichedUser);
    }


    const getEnrichedUserById = (userId?: number): UserModel | undefined => {
        if(!userId) return undefined;
        return getEnrichedUser(userMap.get(userId));
    }


    /**
    * Public API – exposed to consumers of the hook
    */
    return {
        userMap,
        enricherResponse,
        getUserById,
        getEnrichedUser,
        getStrictEnrichedUser,
        getEnrichedUserById,
        getEnrichedUserMap,
    }
}